import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
    Camera, RefreshCw, X, Check, RotateCw, Sparkles,
    Maximize2, FileText, Image as ImageIcon, Sliders,
    ZoomIn, ArrowLeft, SunMedium, Eye, CheckCircle2, Crop,
    Contrast, ArrowRight, Wand2, Loader2
} from 'lucide-react';
import { detectDocumentCornersApi, processDocumentApi } from '../../services/scannerService';

interface Point {
    x: number;
    y: number;
}

interface DocumentScannerModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (file: File) => void;
    initialImage?: string | File | null;
}

type FilterMode = 'magic' | 'bw' | 'original';

const DocumentScannerModal: React.FC<DocumentScannerModalProps> = ({
    isOpen,
    onClose,
    onSave,
    initialImage = null
}) => {
    // Stage: 'camera' | 'crop' | 'preview'
    const [stage, setStage] = useState<'camera' | 'crop' | 'preview'>('camera');
    const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment');
    const [cameraError, setCameraError] = useState<string | null>(null);
    const [hasMultipleCameras, setHasMultipleCameras] = useState(false);

    // Captured image state
    const [capturedImage, setCapturedImage] = useState<HTMLImageElement | null>(null);
    const [capturedImageSrc, setCapturedImageSrc] = useState<string | null>(null);
    const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number }>({ width: 0, height: 0 });

    // 4 Corner Points in original image coordinates [TL, TR, BR, BL]
    const [corners, setCorners] = useState<Point[]>([]);
    const [activeCorner, setActiveCorner] = useState<number | null>(null);
    const [filterMode, setFilterMode] = useState<FilterMode>('magic');
    const [isDetecting, setIsDetecting] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);

    // Live Filtered Preview Data URL
    const [previewFilteredUrl, setPreviewFilteredUrl] = useState<string | null>(null);

    // Magnifier state
    const [magnifierPos, setMagnifierPos] = useState<{ displayX: number; displayY: number; imgX: number; imgY: number } | null>(null);

    // Refs
    const videoRef = useRef<HTMLVideoElement>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const cameraInitialized = useRef(false);

    // ── Iniciar Cámara ──────────────────────────────────────────────────────────
    const startCamera = useCallback(async () => {
        if (cameraInitialized.current) return;
        setCameraError(null);
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }

        try {
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                throw new Error('La cámara no está disponible en este dispositivo.');
            }

            const devices = await navigator.mediaDevices.enumerateDevices();
            const videoDevices = devices.filter(d => d.kind === 'videoinput');
            setHasMultipleCameras(videoDevices.length > 1);

            const constraints: MediaStreamConstraints = {
                audio: false,
                video: {
                    facingMode: { ideal: facingMode },
                    width: { ideal: 1920, min: 1280 },
                    height: { ideal: 1080, min: 720 }
                }
            };

            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                // Esperar a que los metadatos estén listos antes de reproducir
                await new Promise<void>((resolve) => {
                    videoRef.current?.addEventListener('loadedmetadata', () => resolve(), { once: true });
                });
                videoRef.current.play().catch(err => {
                    /* Browser diagnostics omitted. */
                });
                cameraInitialized.current = true;
            }
        } catch (err: any) {
            /* Browser diagnostics omitted. */
            setCameraError(err.message || 'No se pudo acceder a la cámara. Puedes subir una imagen desde tus archivos.');
        }
    }, [facingMode]);

    const stopCamera = useCallback(() => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        cameraInitialized.current = false;
    }, []);

    useEffect(() => {
        if (isOpen) {
            if (initialImage) {
                loadInitialImage(initialImage);
            } else {
                setStage('camera');
                startCamera();
            }
        } else {
            stopCamera();
            setCapturedImage(null);
            setCapturedImageSrc(null);
            setCorners([]);
            setStage('camera');
            setPreviewFilteredUrl(null);
            setMagnifierPos(null);
            setActiveCorner(null);
        }
        return () => {
            stopCamera();
        };
    }, [isOpen, initialImage, startCamera, stopCamera]);

    const defaultCorners = (width: number, height: number): Point[] => {
        const mx = Math.round(width * 0.10);
        const my = Math.round(height * 0.10);
        return [
            { x: mx, y: my },
            { x: width - mx, y: my },
            { x: width - mx, y: height - my },
            { x: mx, y: height - my }
        ];
    };

    // ── Ejecutar Detección Automática de Esquinas con OpenCV (Backend) ──────────
    const triggerAutoDetection = async (imgSrc: string, width: number, height: number) => {
        setIsDetecting(true);
        try {
            const res = await detectDocumentCornersApi(imgSrc);
            if (res && res.corners && res.corners.length === 4) {
                setCorners(res.corners.map(c => ({ x: c[0], y: c[1] })));
            } else {
                setCorners(defaultCorners(width, height));
            }
        } catch (err) {
            /* Browser diagnostics omitted. */
            setCorners(defaultCorners(width, height));
        } finally {
            setIsDetecting(false);
        }
    };

    const loadInitialImage = (imgSource: string | File) => {
        if (imgSource instanceof File) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const dataUrl = e.target?.result as string;
                const img = new Image();
                img.onload = () => {
                    setCapturedImage(img);
                    setCapturedImageSrc(dataUrl);
                    setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
                    setCorners(defaultCorners(img.naturalWidth, img.naturalHeight));
                    setStage('crop');
                    stopCamera();
                    triggerAutoDetection(dataUrl, img.naturalWidth, img.naturalHeight);
                };
                img.src = dataUrl;
            };
            reader.readAsDataURL(imgSource);
        } else {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
                setCapturedImage(img);
                setCapturedImageSrc(imgSource);
                setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
                setCorners(defaultCorners(img.naturalWidth, img.naturalHeight));
                setStage('crop');
                stopCamera();
                triggerAutoDetection(imgSource, img.naturalWidth, img.naturalHeight);
            };
            img.src = imgSource;
        }
    };

    // ── Capturar Fotografía e Iniciar Detección Inmediata ────────────────────────
    const handleCapture = () => {
        if (!videoRef.current) return;
        const video = videoRef.current;
        const width = video.videoWidth || 1920;
        const height = video.videoHeight || 1080;

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        ctx.drawImage(video, 0, 0, width, height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.95);

        const img = new Image();
        img.onload = () => {
            setCapturedImage(img);
            setCapturedImageSrc(dataUrl);
            setImageDimensions({ width, height });
            setCorners(defaultCorners(width, height));
            setStage('crop');
            stopCamera();
            // Detección automática directa con OpenCV
            triggerAutoDetection(dataUrl, width, height);
        };
        img.src = dataUrl;
    };

    const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            loadInitialImage(file);
        }
    };

    const toggleCamera = () => {
        setFacingMode(prev => (prev === 'environment' ? 'user' : 'environment'));
    };

    // ── Rotar Imagen 90 grados y preservar orden exacto de esquinas ───────────
    const handleRotate90 = () => {
        if (!capturedImage) return;

        const { width: oldW, height: oldH } = imageDimensions;
        const newW = oldH;
        const newH = oldW;

        const canvas = document.createElement('canvas');
        canvas.width = newW;
        canvas.height = newH;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        ctx.translate(newW / 2, newH / 2);
        ctx.rotate((90 * Math.PI) / 180);
        ctx.drawImage(capturedImage, -oldW / 2, -oldH / 2);

        const rotatedDataUrl = canvas.toDataURL('image/jpeg', 0.95);
        const rotatedImg = new Image();
        rotatedImg.onload = () => {
            setCapturedImage(rotatedImg);
            setCapturedImageSrc(rotatedDataUrl);
            setImageDimensions({ width: newW, height: newH });

            // Re-mapear esquinas con rotación 90° horaria:
            // (x_rot, y_rot) = (oldH - y, x)
            // Mapeando a nuevo [TL, TR, BR, BL]:
            setCorners(prev => {
                if (prev.length !== 4) return defaultCorners(newW, newH);
                return [
                    { x: Math.max(0, Math.min(newW, oldH - prev[3].y)), y: Math.max(0, Math.min(newH, prev[3].x)) }, // TL
                    { x: Math.max(0, Math.min(newW, oldH - prev[0].y)), y: Math.max(0, Math.min(newH, prev[0].x)) }, // TR
                    { x: Math.max(0, Math.min(newW, oldH - prev[1].y)), y: Math.max(0, Math.min(newH, prev[1].x)) }, // BR
                    { x: Math.max(0, Math.min(newW, oldH - prev[2].y)), y: Math.max(0, Math.min(newH, prev[2].x)) }  // BL
                ];
            });
        };
        rotatedImg.src = rotatedDataUrl;
    };

    // ── Botón Auto-Ajustar a la Hoja ──────────────────────────────────────────
    const handleAutoCrop = () => {
        if (!capturedImageSrc) return;
        triggerAutoDetection(capturedImageSrc, imageDimensions.width, imageDimensions.height);
    };

    const handleResetAll = () => {
        if (!capturedImage) return;
        setCorners([
            { x: 0, y: 0 },
            { x: imageDimensions.width, y: 0 },
            { x: imageDimensions.width, y: imageDimensions.height },
            { x: 0, y: imageDimensions.height }
        ]);
    };

    // ── Manejo de Arrastre de Esquinas y Lupa de Precisión ─────────────────────
    const handlePointerDown = (index: number, e: React.PointerEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setActiveCorner(index);
        (e.target as HTMLElement).setPointerCapture(e.pointerId);

        if (containerRef.current && capturedImage) {
            const rect = containerRef.current.getBoundingClientRect();
            const displayX = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
            const displayY = Math.max(0, Math.min(e.clientY - rect.top, rect.height));

            const scaleX = imageDimensions.width / rect.width;
            const scaleY = imageDimensions.height / rect.height;

            const imgX = Math.round(displayX * scaleX);
            const imgY = Math.round(displayY * scaleY);

            setMagnifierPos({ displayX, displayY, imgX, imgY });
        }
    };

    const handlePointerMove = (e: React.PointerEvent) => {
        if (activeCorner === null || !containerRef.current || !capturedImage) return;

        const rect = containerRef.current.getBoundingClientRect();
        const displayX = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
        const displayY = Math.max(0, Math.min(e.clientY - rect.top, rect.height));

        const scaleX = imageDimensions.width / rect.width;
        const scaleY = imageDimensions.height / rect.height;

        const imgX = Math.round(displayX * scaleX);
        const imgY = Math.round(displayY * scaleY);

        setCorners(prev => {
            const next = [...prev];
            next[activeCorner] = {
                x: Math.max(0, Math.min(imgX, imageDimensions.width)),
                y: Math.max(0, Math.min(imgY, imageDimensions.height))
            };
            return next;
        });

        setMagnifierPos({ displayX, displayY, imgX, imgY });
    };

    const handlePointerUp = (e: React.PointerEvent) => {
        if (activeCorner !== null) {
            try {
                (e.target as HTMLElement).releasePointerCapture(e.pointerId);
            } catch {}
            setActiveCorner(null);
            setMagnifierPos(null);
        }
    };

    // ── Fallback Local en caso de demoras de red ───────────────────────────────
    const fallbackLocalWarp = (targetFilter: FilterMode) => {
        if (!capturedImage || corners.length !== 4) return;
        try {
            const [tl, tr, br, bl] = corners;
            const w1 = Math.hypot(tr.x - tl.x, tr.y - tl.y);
            const w2 = Math.hypot(br.x - bl.x, br.y - bl.y);
            const targetW = Math.max(300, Math.round(Math.max(w1, w2)));

            const h1 = Math.hypot(bl.x - tl.x, bl.y - tl.y);
            const h2 = Math.hypot(br.x - tr.x, br.y - tr.y);
            const targetH = Math.max(300, Math.round(Math.max(h1, h2)));

            const canvas = document.createElement('canvas');
            canvas.width = targetW;
            canvas.height = targetH;
            const ctx = canvas.getContext('2d');
            if (!ctx) return;

            const minX = Math.min(tl.x, tr.x, br.x, bl.x);
            const minY = Math.min(tl.y, tr.y, br.y, bl.y);
            const maxX = Math.max(tl.x, tr.x, br.x, bl.x);
            const maxY = Math.max(tl.y, tr.y, br.y, bl.y);
            const cropW = Math.max(10, maxX - minX);
            const cropH = Math.max(10, maxY - minY);

            ctx.drawImage(capturedImage, minX, minY, cropW, cropH, 0, 0, targetW, targetH);

            if (targetFilter === 'magic') {
                ctx.filter = 'contrast(130%) brightness(105%)';
                ctx.drawImage(canvas, 0, 0);
            } else if (targetFilter === 'bw') {
                ctx.filter = 'grayscale(100%) contrast(150%) brightness(105%)';
                ctx.drawImage(canvas, 0, 0);
            }

            const fallbackUrl = canvas.toDataURL('image/webp', 0.9);
            setPreviewFilteredUrl(fallbackUrl);
        } catch (e) {
            /* Browser diagnostics omitted. */
        }
    };

    // ── Procesar Vista Previa con OpenCV y Filtros ────────────────────────────
    const updatePreviewWithFilter = async (targetFilter: FilterMode) => {
        if (!capturedImageSrc || corners.length !== 4) return;
        setIsProcessing(true);

        try {
            const { dataUrl } = await processDocumentApi(
                capturedImageSrc,
                corners.map(c => [c.x, c.y]),
                targetFilter
            );
            setPreviewFilteredUrl(dataUrl);
        } catch (err) {
            /* Browser diagnostics omitted. */
            fallbackLocalWarp(targetFilter);
        } finally {
            setIsProcessing(false);
        }
    };

    // Ir a la etapa de vista previa con filtro en vivo
    const handleGoToPreview = async () => {
        setStage('preview');
        setIsProcessing(true);
        await updatePreviewWithFilter(filterMode);
    };

    // Cambiar filtro en vivo
    const handleChangeFilter = async (newFilter: FilterMode) => {
        setFilterMode(newFilter);
        await updatePreviewWithFilter(newFilter);
    };

    // ── Guardar y Exportar como WebP ──────────────────────────────────────────
    const handleConfirmAndSave = async () => {
        if (!capturedImageSrc || corners.length !== 4) return;
        setIsProcessing(true);

        try {
            const { blob } = await processDocumentApi(
                capturedImageSrc,
                corners.map(c => [c.x, c.y]),
                filterMode
            );

            const file = new File([blob], `declaracion_jurada_${Date.now()}.webp`, {
                type: 'image/webp',
                lastModified: Date.now()
            });

            onSave(file);
            onClose();
        } catch (err) {
            /* Browser diagnostics omitted. */
            if (previewFilteredUrl) {
                try {
                    const res = await fetch(previewFilteredUrl);
                    const blob = await res.blob();
                    const file = new File([blob], `declaracion_jurada_${Date.now()}.webp`, {
                        type: 'image/webp',
                        lastModified: Date.now()
                    });
                    onSave(file);
                    onClose();
                    return;
                } catch (e) {
                    /* Browser diagnostics omitted. */
                }
            }
        } finally {
            setIsProcessing(false);
        }
    };

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-slate-950/90 backdrop-blur-md animate-in fade-in duration-200">
            <div className="bg-slate-900 w-full max-w-4xl h-[94vh] rounded-2xl shadow-2xl border border-slate-800 flex flex-col overflow-hidden relative text-white">
                
                {/* ── Barra Superior con Progreso ── */}
                <div className="px-5 py-3.5 bg-slate-900/95 border-b border-slate-800 flex items-center justify-between z-20">
                    <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-xl bg-gradient-to-tr from-[#61257b] to-[#c1348b] flex items-center justify-center shadow-md shadow-[#61257b]/30">
                            <Wand2 className="h-5 w-5 text-white" />
                        </div>
                        <div>
                            <h3 className="text-sm font-bold text-white flex items-center gap-2">
                                Escáner Inteligente CamScanner
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#61257b]/30 text-[#ffd97c] font-semibold border border-[#61257b]/40 flex items-center gap-1">
                                    {isDetecting ? (
                                        <>
                                            <Loader2 className="h-2.5 w-2.5 animate-spin" />
                                            Detectando con OpenCV...
                                        </>
                                    ) : (
                                        'OpenCV AI Activo'
                                    )}
                                </span>
                            </h3>
                            <p className="text-xs text-slate-400">
                                {stage === 'camera' && '1. Encuadra la hoja de la declaración jurada'}
                                {stage === 'crop' && '2. Bordes detectados automáticamente (puedes mover las esquinas con la lupa de precisión)'}
                                {stage === 'preview' && '3. Revisa la mejora en vivo y selecciona el filtro'}
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        {stage === 'crop' && (
                            <button
                                type="button"
                                onClick={() => {
                                    setStage('camera');
                                    startCamera();
                                }}
                                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-800 hover:bg-slate-700 text-slate-300 transition-colors flex items-center gap-1.5 cursor-pointer"
                            >
                                <ArrowLeft className="h-3.5 w-3.5" /> Re-tomar
                            </button>
                        )}
                        {stage === 'preview' && (
                            <button
                                type="button"
                                onClick={() => setStage('crop')}
                                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-800 hover:bg-slate-700 text-slate-300 transition-colors flex items-center gap-1.5 cursor-pointer"
                            >
                                <Crop className="h-3.5 w-3.5 text-[#8b3fa8]" /> Modificar Recorte
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={onClose}
                            className="h-8 w-8 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white flex items-center justify-center transition-colors cursor-pointer"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                </div>

                {/* ── Contenido Principal ── */}
                <div className="flex-1 relative overflow-hidden bg-slate-950 flex items-center justify-center p-3">
                    
                    {/* 1. MODO CÁMARA */}
                    {stage === 'camera' && (
                        <div className="relative w-full h-full flex items-center justify-center overflow-hidden rounded-xl bg-black">
                            <video
                                ref={videoRef}
                                autoPlay
                                playsInline
                                muted
                                className="w-full h-full object-contain"
                            />

                            {/* Guía de Encuadre */}
                            <div className="absolute inset-0 pointer-events-none flex items-center justify-center p-6 sm:p-12">
                                <div className="relative w-full max-w-md aspect-[3/4] max-h-[75vh] rounded-xl border-2 border-dashed border-[#8b3fa8]/80 shadow-[0_0_0_9999px_rgba(0,0,0,0.6)] flex flex-col justify-between p-4">
                                    <div className="absolute -top-1 -left-1 h-6 w-6 border-t-4 border-l-4 border-[#c1348b] rounded-tl-lg" />
                                    <div className="absolute -top-1 -right-1 h-6 w-6 border-t-4 border-r-4 border-[#c1348b] rounded-tr-lg" />
                                    <div className="absolute -bottom-1 -left-1 h-6 w-6 border-b-4 border-l-4 border-[#c1348b] rounded-bl-lg" />
                                    <div className="absolute -bottom-1 -right-1 h-6 w-6 border-b-4 border-r-4 border-[#c1348b] rounded-br-lg" />

                                    <div className="w-full h-0.5 bg-gradient-to-r from-transparent via-[#8b3fa8] to-transparent shadow-[0_0_12px_#8b3fa8] animate-bounce" />

                                    <div className="text-center bg-black/70 backdrop-blur-md px-3.5 py-1.5 rounded-full self-center border border-white/10 shadow-lg">
                                        <p className="text-xs font-bold text-emerald-300">Auto-detección con OpenCV al capturar</p>
                                    </div>
                                </div>
                            </div>

                            {cameraError && (
                                <div className="absolute inset-0 bg-slate-900/90 flex flex-col items-center justify-center p-6 text-center z-30">
                                    <Camera className="h-12 w-12 text-rose-500 mb-3" />
                                    <h4 className="text-base font-bold text-white mb-1">Cámara no disponible</h4>
                                    <p className="text-xs text-slate-400 max-w-sm mb-4">{cameraError}</p>
                                    <button
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                        className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#61257b] to-[#c1348b] text-white text-xs font-bold shadow-lg hover:opacity-95 transition-all flex items-center gap-2 cursor-pointer"
                                    >
                                        <ImageIcon className="h-4 w-4" /> Seleccionar Imagen de Galería
                                    </button>
                                </div>
                            )}

                            {/* Controles sobre la cámara */}
                            <div className="absolute bottom-6 inset-x-0 flex items-center justify-center gap-6 z-20">
                                {hasMultipleCameras && (
                                    <button
                                        type="button"
                                        onClick={toggleCamera}
                                        className="h-12 w-12 rounded-full bg-slate-800/80 hover:bg-slate-700 text-white backdrop-blur-md flex items-center justify-center border border-white/10 transition-transform active:scale-95 cursor-pointer"
                                        title="Cambiar Cámara"
                                    >
                                        <RefreshCw className="h-5 w-5" />
                                    </button>
                                )}

                                <button
                                    type="button"
                                    onClick={handleCapture}
                                    className="h-18 w-18 rounded-full bg-white p-1.5 shadow-2xl hover:scale-105 active:scale-95 transition-all flex items-center justify-center cursor-pointer border-4 border-[#c1348b]"
                                    title="Capturar y Auto-Detectar"
                                >
                                    <div className="h-full w-full rounded-full bg-gradient-to-tr from-[#8f1f64] to-[#c1348b] flex items-center justify-center">
                                        <Camera className="h-7 w-7 text-white" />
                                    </div>
                                </button>

                                <button
                                    type="button"
                                    onClick={() => fileInputRef.current?.click()}
                                    className="h-12 w-12 rounded-full bg-slate-800/80 hover:bg-slate-700 text-white backdrop-blur-md flex items-center justify-center border border-white/10 transition-transform active:scale-95 cursor-pointer"
                                    title="Subir de galería"
                                >
                                    <ImageIcon className="h-5 w-5" />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* 2. MODO RECORTE DE 4 ESQUINAS AUTO-AJUSTADAS CON LUPA DE PRECISIÓN */}
                    {stage === 'crop' && capturedImage && (
                        <div className="relative w-full h-full flex items-center justify-center p-6 select-none touch-none">
                            <div
                                ref={containerRef}
                                className="relative max-w-full max-h-[75vh] select-none touch-none aspect-auto cursor-crosshair shadow-2xl rounded-lg border border-slate-700 bg-black"
                                onPointerMove={handlePointerMove}
                                onPointerUp={handlePointerUp}
                                onPointerLeave={handlePointerUp}
                            >
                                <img
                                    src={capturedImage.src}
                                    alt="Captura a recortar"
                                    className="max-h-[75vh] w-auto object-contain block pointer-events-none rounded-lg"
                                    draggable={false}
                                />

                                {/* Capa Máscara SVG con el polígono */}
                                <svg className="absolute inset-0 w-full h-full pointer-events-none rounded-lg overflow-visible">
                                    <defs>
                                        <mask id="doc-mask-auto">
                                            <rect width="100%" height="100%" fill="white" />
                                            <polygon
                                                points={corners
                                                    .map(p => {
                                                        const pctX = (p.x / imageDimensions.width) * (containerRef.current?.clientWidth || 1);
                                                        const pctY = (p.y / imageDimensions.height) * (containerRef.current?.clientHeight || 1);
                                                        return `${pctX},${pctY}`;
                                                    })
                                                    .join(' ')}
                                                fill="black"
                                            />
                                        </mask>
                                    </defs>

                                    <rect width="100%" height="100%" fill="rgba(0, 0, 0, 0.65)" mask="url(#doc-mask-auto)" />

                                    <polygon
                                        points={corners
                                            .map(p => {
                                                const pctX = (p.x / imageDimensions.width) * (containerRef.current?.clientWidth || 1);
                                                const pctY = (p.y / imageDimensions.height) * (containerRef.current?.clientHeight || 1);
                                                return `${pctX},${pctY}`;
                                            })
                                            .join(' ')}
                                        fill="rgba(139, 63, 168, 0.08)"
                                        stroke="#8b3fa8"
                                        strokeWidth="3"
                                        strokeDasharray="5 3"
                                    />
                                </svg>

                                {/* 4 Controles de Esquina Interactivos */}
                                {corners.map((p, idx) => {
                                    const containerW = containerRef.current?.clientWidth || 1;
                                    const containerH = containerRef.current?.clientHeight || 1;
                                    const rawX = (p.x / imageDimensions.width) * containerW;
                                    const rawY = (p.y / imageDimensions.height) * containerH;

                                    const posX = Math.max(0, Math.min(rawX, containerW));
                                    const posY = Math.max(0, Math.min(rawY, containerH));

                                    const cornerLabels = ['Sup-Izq', 'Sup-Der', 'Inf-Der', 'Inf-Izq'];

                                    return (
                                        <div
                                            key={idx}
                                            onPointerDown={e => handlePointerDown(idx, e)}
                                            style={{
                                                transform: `translate3d(${posX - 18}px, ${posY - 18}px, 0)`,
                                                touchAction: 'none'
                                            }}
                                            className={`absolute top-0 left-0 w-9 h-9 rounded-full border-2 border-white flex items-center justify-center cursor-move transition-transform shadow-2xl z-40 ${
                                                activeCorner === idx
                                                    ? 'bg-[#c1348b] scale-125 shadow-[0_0_20px_#c1348b]'
                                                    : 'bg-[#61257b] hover:scale-115 hover:bg-[#8b3fa8]'
                                            }`}
                                            title={cornerLabels[idx]}
                                        >
                                            <div className="w-2.5 h-2.5 rounded-full bg-white shadow-xs" />
                                            <div className="absolute -inset-3 rounded-full pointer-events-auto" />
                                        </div>
                                    );
                                })}

                                {/* Lupa de Precisión en Vivo (Zoom 2.8x con Retícula) */}
                                {magnifierPos && activeCorner !== null && (
                                    <div
                                        style={{
                                            left: Math.min(Math.max(10, magnifierPos.displayX - 60), (containerRef.current?.clientWidth || 100) - 130),
                                            top: Math.max(10, magnifierPos.displayY - 140),
                                            width: '120px',
                                            height: '120px'
                                        }}
                                        className="absolute pointer-events-none rounded-full border-4 border-[#8b3fa8] overflow-hidden shadow-2xl bg-black z-50 flex items-center justify-center"
                                    >
                                        <div
                                            style={{
                                                backgroundImage: `url(${capturedImageSrc || capturedImage.src})`,
                                                backgroundSize: `${(containerRef.current?.clientWidth || 1) * 2.8}px ${(containerRef.current?.clientHeight || 1) * 2.8}px`,
                                                backgroundPosition: `-${magnifierPos.displayX * 2.8 - 60}px -${magnifierPos.displayY * 2.8 - 60}px`,
                                                backgroundRepeat: 'no-repeat',
                                                width: '100%',
                                                height: '100%'
                                            }}
                                        />
                                        {/* Cruz de mira de alta precisión */}
                                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                            <div className="w-full h-0.5 bg-[#c1348b] shadow-sm" />
                                            <div className="absolute h-full w-0.5 bg-[#c1348b] shadow-sm" />
                                            <div className="w-3 h-3 rounded-full border border-[#c1348b] shadow-sm" />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* 3. MODO VISTA PREVIA CON FILTRO EN VIVO */}
                    {stage === 'preview' && (
                        <div className="relative w-full h-full flex flex-col items-center justify-center overflow-hidden">
                            {isProcessing ? (
                                <div className="flex flex-col items-center gap-3">
                                    <Loader2 className="h-10 w-10 text-[#8b3fa8] animate-spin" />
                                    <p className="text-xs text-slate-300 font-semibold">Procesando y aplicando realce OpenCV...</p>
                                </div>
                            ) : previewFilteredUrl ? (
                                <div className="relative max-h-[72vh] rounded-xl overflow-hidden shadow-2xl border border-white/20 bg-slate-900">
                                    <img
                                        src={previewFilteredUrl}
                                        alt="Documento escaneado y mejorado"
                                        className="max-h-[70vh] w-auto object-contain block"
                                    />
                                    <div className="absolute top-3 right-3 px-2.5 py-1 rounded-md bg-black/70 backdrop-blur-sm text-[11px] font-bold text-[#8b3fa8] border border-white/10 flex items-center gap-1.5">
                                        <Sparkles className="h-3 w-3" />
                                        {filterMode === 'magic' ? 'Filtro: Magic Color' : filterMode === 'bw' ? 'Filtro: B/N Escáner' : 'Filtro: Original'}
                                    </div>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center gap-3 text-slate-400">
                                    <p className="text-sm font-semibold">No se pudo cargar la vista previa</p>
                                    <button
                                        type="button"
                                        onClick={() => updatePreviewWithFilter(filterMode)}
                                        className="px-4 py-2 rounded-xl bg-slate-800 text-xs font-bold text-white hover:bg-slate-700 transition-colors cursor-pointer"
                                    >
                                        Reintentar Procesamiento
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* ── Barra Inferior: Herramientas en Etapa de Recorte ── */}
                {stage === 'crop' && (
                    <div className="px-5 py-3.5 bg-slate-900 border-t border-slate-800 flex flex-wrap items-center justify-between gap-3 z-20">
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={handleRotate90}
                                title="Rotar 90°"
                                className="px-3 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-xs font-semibold text-slate-200 transition-colors flex items-center gap-1.5 cursor-pointer"
                            >
                                <RotateCw className="h-4 w-4 text-[#8b3fa8]" />
                                Rotar
                            </button>
                            <button
                                type="button"
                                onClick={handleAutoCrop}
                                disabled={isDetecting}
                                title="Auto-Detectar Bordes de Hoja con OpenCV"
                                className="px-3 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-xs font-semibold text-slate-200 transition-colors flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                            >
                                {isDetecting ? (
                                    <Loader2 className="h-4 w-4 text-amber-400 animate-spin" />
                                ) : (
                                    <Sparkles className="h-4 w-4 text-amber-400" />
                                )}
                                Auto-Ajustar (OpenCV)
                            </button>
                            <button
                                type="button"
                                onClick={handleResetAll}
                                title="Expandir Todo"
                                className="px-3 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-xs font-semibold text-slate-200 transition-colors cursor-pointer"
                            >
                                Toda la foto
                            </button>
                        </div>

                        {/* Botón Siguiente a Vista Previa */}
                        <button
                            type="button"
                            onClick={handleGoToPreview}
                            className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-[#61257b] to-[#c1348b] hover:opacity-95 text-white font-black text-xs shadow-lg shadow-[#61257b]/25 transition-all flex items-center gap-2 cursor-pointer"
                        >
                            Siguiente (Ver Mejoras) <ArrowRight className="h-4 w-4" />
                        </button>
                    </div>
                )}

                {/* ── Barra Inferior: Filtros en Vivo y Guardar (Etapa Preview) ── */}
                {stage === 'preview' && (
                    <div className="px-5 py-3.5 bg-slate-900 border-t border-slate-800 flex flex-wrap items-center justify-between gap-3 z-20">
                        {/* Selector de Filtros en Tiempo Real */}
                        <div className="flex items-center gap-2 bg-slate-950 p-1.5 rounded-xl border border-slate-800">
                            {[
                                { id: 'magic', label: 'Magic Color (Recomendado)', icon: <Sparkles className="h-4 w-4 text-amber-400" /> },
                                { id: 'bw', label: 'B/N Escáner', icon: <FileText className="h-4 w-4 text-slate-300" /> },
                                { id: 'original', label: 'Original', icon: <ImageIcon className="h-4 w-4 text-purple-300" /> }
                            ].map(f => (
                                <button
                                    key={f.id}
                                    type="button"
                                    onClick={() => handleChangeFilter(f.id as FilterMode)}
                                    className={`px-3.5 py-2 rounded-lg text-xs font-bold flex items-center gap-2 transition-all cursor-pointer ${
                                        filterMode === f.id
                                            ? 'bg-gradient-to-r from-[#61257b] to-[#c1348b] text-white shadow-md'
                                            : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/80'
                                    }`}
                                >
                                    {f.icon}
                                    {f.label}
                                </button>
                            ))}
                        </div>

                        {/* Botón Final: Guardar */}
                        <button
                            type="button"
                            onClick={handleConfirmAndSave}
                            disabled={isProcessing}
                            className="px-7 py-2.5 rounded-xl bg-gradient-to-r from-[#61257b] to-[#c1348b] hover:opacity-95 text-white font-black text-xs shadow-xl shadow-[#61257b]/30 transition-all flex items-center gap-2 disabled:opacity-60 cursor-pointer"
                        >
                            {isProcessing ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Guardando Declaración...
                                </>
                            ) : (
                                <>
                                    <CheckCircle2 className="h-4 w-4" />
                                    Guardar Declaración
                                </>
                            )}
                        </button>
                    </div>
                )}

                {/* Input archivo oculto */}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleFileSelected}
                />
            </div>
        </div>,
        document.body
    );
};

export default DocumentScannerModal;
